import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const pressureCards = [
  {
    title: 'Sickness absence',
    meaning: 'Rising absence may be linked to workload, management, health, morale or factors outside work. The figures show where to look, but not always why it is happening.',
    help: 'Ollie brings together absence data, employee experience and leadership insight to identify patterns and decide what needs closer attention.',
  },
  {
    title: 'Staff turnover',
    meaning: 'People may leave because of pay, progression, management, workload, job design or changes outside the organisation. Exit figures alone rarely explain the full picture.',
    help: 'Ollie looks at turnover alongside employee feedback, leadership decisions and the way work is organised to help identify what the organisation can influence.',
  },
  {
    title: 'Low trust',
    meaning: 'Employees may hold back concerns, question leadership intentions or feel that previous feedback has not led to action. Trust can also vary greatly between teams.',
    help: 'Ollie creates clear and confidential ways for employees to share their experience, then helps leaders understand where trust is strong and where it is being weakened.',
  },
  {
    title: 'Poor communication',
    meaning: 'The issue may not be a lack of messages. Employees may receive information too late, through the wrong channel or without enough explanation of what it means for them.',
    help: 'Ollie examines how information moves through the organisation, where it becomes unclear and how managers can communicate more consistently.',
  },
  {
    title: 'Change fatigue',
    meaning: 'Employees may be dealing with repeated changes, unclear priorities or decisions that appear disconnected from their daily work. Even positive change can create pressure.',
    help: 'Ollie helps leaders understand how change is being experienced, what employees need to know and what should be protected while the organisation moves forward.',
  },
  {
    title: 'Management inconsistency',
    meaning: 'Employees may have very different experiences depending on their manager, shift, team or location. Expectations may be clear at leadership level but applied unevenly.',
    help: 'Ollie identifies where management practice differs, what managers need more help with and where clearer expectations or practical support may improve consistency.',
  },
];

const ProblemSection = () => {
  const [openCards, setOpenCards] = useState(new Set());

  const toggleCard = (idx) => {
    setOpenCards(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <section className="py-20 md:py-28 bg-white" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto lg:mx-0">
          <motion.h2
            className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-carbon mb-6 tracking-tight"
            {...fadeInUp}
          >
            YOU SEE THE <span className="yellow-highlight text-carbon">SIGNS</span> BEFORE YOU SEE THE <span className="yellow-highlight text-carbon">CAUSE</span>
          </motion.h2>

          <motion.p className="text-base text-carbon/80 mb-4 leading-relaxed" {...fadeInUp}>
            They often appear as rising absence, people leaving, managers handling the same issue differently, teams holding back concerns, or employees losing confidence during change.
          </motion.p>

          <motion.p className="text-base text-carbon/80 mb-10 leading-relaxed" {...fadeInUp}>
            These may look like separate problems. Often, they are signals that something in the way work is organised, led or communicated needs attention.
          </motion.p>
        </div>

        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={{ animate: { transition: { staggerChildren: 0.06 } } }}
        >
          {pressureCards.map((card, idx) => {
            const isOpen = openCards.has(idx);
            return (
              <motion.div
                key={card.title}
                className="bg-gray-50 border border-gray-100"
                variants={{
                  initial: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0 }
                }}
              >
                <button
                  onClick={() => toggleCard(idx)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-golden focus-visible:ring-offset-2"
                  aria-expanded={isOpen}
                  aria-controls={`pressure-panel-${idx}`}
                  data-testid={`pressure-card-${idx}`}
                >
                  <span className="text-sm font-medium text-carbon">{card.title}</span>
                  {isOpen
                    ? <Minus className="w-4 h-4 text-carbon/50 flex-shrink-0" />
                    : <Plus className="w-4 h-4 text-carbon/50 flex-shrink-0" />
                  }
                </button>
                {isOpen && (
                  <div
                    id={`pressure-panel-${idx}`}
                    className="px-5 pb-5 space-y-4"
                    role="region"
                    aria-label={card.title}
                  >
                    <div>
                      <h4 className="text-xs font-semibold text-carbon/50 uppercase tracking-wide mb-1">What this can mean</h4>
                      <p className="text-sm text-carbon/75 leading-relaxed">{card.meaning}</p>
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold text-carbon/50 uppercase tracking-wide mb-1">How Ollie helps</h4>
                      <p className="text-sm text-carbon/75 leading-relaxed">{card.help}</p>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;
